import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { switchMap, tap, catchError, timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../logger/logger.service';
import { TokenResponse } from '@nationwide/dgs-angular-billing-common';
import { EUAv3PKCESharedService } from '../euav3-pkce/euav3-pkce-shared.service';
import { IframeQueueService } from '../../iframe-queue.service';

export interface AuthResult {
    successful: boolean;
    error?: string;
}

const AUTH_FLOW_TIMEOUT_MS = 30000;

@Injectable({
    providedIn: 'root'
})
export class EUAv3IframeService {
    constructor(
        private logger: LoggerService,
        private pkceService: EUAv3PKCESharedService,
        private iframeQueueService: IframeQueueService
    ) { }

    initiateIframeAuth(): Observable<AuthResult> {
        this.logger.info('Initiating EUA3 iframe PKCE auth flow');

        return from(this.generatePKCEAndBuildUrl()).pipe(
            switchMap(authorizeUrl => this.loadAuthInIframe(authorizeUrl)),
            switchMap(authResponse => this.handleIframeResponse(authResponse)),
            timeout({ first: AUTH_FLOW_TIMEOUT_MS }),
            tap(() => {
                this.logger.info('Iframe auth flow completed successfully');
            }),
            catchError(error => {
                this.logger.error('Iframe auth flow failed', error);
                return throwError(() => error);
            })
        );
    }

    refreshToken(): Observable<TokenResponse> {
        const refreshToken = sessionStorage.getItem('refresh_token');

        if (!refreshToken) {
            this.logger.error('No refresh token available for token renewal');
            return throwError(() => new Error('No refresh token available'));
        }

        this.logger.info('Refreshing token using refresh_token grant');
        return this.pkceService.refreshToken(refreshToken).pipe(
            tap(() => {
                this.logger.info('Token refresh completed, fetching user info');
                const newAccessToken = sessionStorage.getItem('access_token');
                if (newAccessToken) {
                    this.pkceService.fetchUserInfo(newAccessToken).subscribe({
                        next: (userInfo) => {
                            this.pkceService.storeTokenData(
                                { access_token: newAccessToken, expires_in: 0, token_type: 'Bearer' },
                                userInfo
                            );
                        },
                        error: (error) => {
                            this.logger.error('Failed to fetch updated user info after refresh', error);
                        }
                    });
                }
            }),
            catchError(error => {
                this.logger.error('Token refresh failed', error);
                return throwError(() => error);
            })
        );
    }

    private async generatePKCEAndBuildUrl(): Promise<string> {
        const verifier = this.pkceService.generatePKCEVerifier();
        const challenge = await this.pkceService.generatePKCEChallenge(verifier);
        const state = this.pkceService.generateState();

        sessionStorage.setItem('pkce_verifier', verifier);
        sessionStorage.setItem('pkce_state', state);

        const params = new URLSearchParams({
            client_id: environment.EUAV3_CONFIG.clientId,
            redirect_uri: environment.EUAV3_CONFIG.redirectUri,
            response_type: 'code',
            scope: environment.EUAV3_CONFIG.scope,
            state: state,
            code_challenge: challenge,
            code_challenge_method: 'S256',
            nonce: this.pkceService.generateState()
        });

        const authorizeUrl = `${environment.EUAV3_CONFIG.authorizeEndpoint}?${params.toString()}`;
        this.logger.info('PKCE authorize URL built');

        return authorizeUrl;
    }

    private loadAuthInIframe(authorizeUrl: string): Observable<string> {
        this.logger.info('Loading authorize URL in iframe');

        this.iframeQueueService.nextIn(authorizeUrl);

        return new Observable(observer => {
            const subscription = this.iframeQueueService.listenForIframeQueueOutEvent().subscribe({
                next: (response) => {
                    if (response) {
                        this.logger.info('Received iframe response');
                        observer.next(response);
                        observer.complete();
                    }
                },
                error: (error) => {
                    this.logger.error('Error listening for iframe response', error);
                    observer.error(error);
                }
            });

            return () => subscription.unsubscribe();
        });
    }

    private handleIframeResponse(responseUrl: string): Observable<AuthResult> {
        this.logger.info('Processing iframe auth response');

        try {
            const url = new URL(responseUrl, window.location.origin);
            const code = url.searchParams.get('code') || this.getHashParam(responseUrl, 'code');
            const state = url.searchParams.get('state') || this.getHashParam(responseUrl, 'state');

            if (!code) {
                this.logger.error('No authorization code in iframe response');
                return throwError(() => new Error('No authorization code in response'));
            }

            this.logger.info('Authorization code extracted from iframe response');

            const savedState = sessionStorage.getItem('pkce_state');
            if (state && savedState && state !== savedState) {
                this.logger.error('State mismatch in iframe auth response');
                return throwError(() => new Error('State mismatch - potential CSRF attack'));
            }

            const verifier = sessionStorage.getItem('pkce_verifier');
            if (!verifier) {
                this.logger.error('No PKCE verifier found in session');
                return throwError(() => new Error('No PKCE verifier in session'));
            }

            return this.pkceService.exchangeCodeForToken(
                code,
                verifier,
                environment.EUAV3_CONFIG.redirectUri
            ).pipe(
                switchMap(tokenResponse => {
                    this.logger.info('Tokens received from auth server, fetching user info');

                    return this.pkceService.fetchUserInfo(tokenResponse.access_token).pipe(
                        tap(userInfo => {
                            this.logger.info('User info fetched, storing all data');
                            this.pkceService.storeTokenData(tokenResponse, userInfo);
                            this.cleanupPKCESession();
                        }),
                        switchMap(() => of({ successful: true } as AuthResult)),
                        catchError(error => {
                            this.logger.warn('Failed to fetch user info, but tokens were obtained', error);
                            this.pkceService.storeTokenData(tokenResponse, { sub: 'unknown' });
                            this.cleanupPKCESession();
                            return of({ successful: true } as AuthResult);
                        })
                    );
                }),
                catchError(error => {
                    this.logger.error('Code exchange failed in iframe auth', error);
                    this.cleanupPKCESession();
                    return throwError(() => error);
                })
            );
        } catch (error) {
            const loggerPayload: object = error instanceof Error ? error : { error };
            this.logger.error('Error processing iframe response', loggerPayload);
            return throwError(() => error);
        }
    }

    private getHashParam(url: string, param: string): string | null {
        try {
            const hashPart = url.split('#')[1];
            if (!hashPart) { return null; }
            const params = new URLSearchParams(hashPart);
            return params.get(param);
        } catch {
            return null;
        }
    }

    private cleanupPKCESession(): void {
        sessionStorage.removeItem('pkce_verifier');
        sessionStorage.removeItem('pkce_state');
    }
}
